"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Facebook, Linkedin, Instagram, Loader2, Check } from "lucide-react";

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15.2a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.73a8.19 8.19 0 0 0 4.76 1.52V6.8a4.84 4.84 0 0 1-1-.11z" />
    </svg>
  );
}

const footerLinks: Record<string, { title: string; links: { name: string; href: string; external?: boolean; cookie?: boolean }[] }> = {
  toolouer: {
    title: "Toolouer",
    links: [
      { name: "Comment ça marche", href: "/comment-ca-marche" },
      { name: "Nos marques", href: "/nos-marques" },
      { name: "Devenez partenaire ?", href: "/devenez-partenaire" },
      { name: "FAQ", href: "/faq" },
      { name: "Blog", href: "/blog" },
    ],
  },
  services: {
    title: "Nos services",
    links: [
      { name: "Location de Matériel", href: "/categories/location" },
      { name: "Vente de Matériel", href: "/categories/achat" },
      { name: "Actes administratifs", href: "/categories/acte-administratif" },
      { name: "Nos types de livraison", href: "/nos-types-de-livraison" },
      { name: "Réparation de Matériel", href: "/reparation-de-materiel" },
    ],
  },
  support: {
    title: "Support et assistance",
    links: [
      { name: "Notre réseau d'agences", href: "/notre-reseau-dagences" },
      { name: "Contactez-nous", href: "/contact-us" },
      { name: "Mentions légales", href: "/docs/mentions-legales.pdf", external: true },
      { name: "CGU Marketplace", href: "/docs/cgu.pdf", external: true },
      { name: "CGU Mollie", href: "https://www.mollie.com/fr/legal/user-agreement", external: true },
      { name: "Contact RGPD : groupe@toolouer.com", href: "mailto:groupe@toolouer.com" },
    ],
  },
};

const socialLinks = [
  { name: "Instagram", icon: Instagram, href: "https://www.instagram.com/toolouer/" },
  { name: "Facebook", icon: Facebook, href: "https://www.facebook.com/rslocationofficiel/?locale=fr_FR" },
  { name: "LinkedIn", icon: Linkedin, href: "https://www.linkedin.com/company/toolouer/" },
  { name: "TikTok", icon: TikTokIcon, href: "https://www.tiktok.com/@toolouer" },
];

export function Footer() {
  const [nlEmail, setNlEmail] = useState("");
  const [nlStatus, setNlStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [nlError, setNlError] = useState("");

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nlEmail.trim()) return;

    setNlStatus("loading");
    setNlError("");

    try {
      const res = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: nlEmail.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Erreur");
      }

      setNlStatus("success");
      setNlEmail("");
    } catch (err) {
      setNlStatus("error");
      setNlError(err instanceof Error ? err.message : "Erreur, veuillez réessayer");
    }
  };

  return (
    <footer className="bg-white border-t">
      {/* Newsletter */}
      <div className="bg-secondary">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-xl mx-auto text-center">
            <h3 className="text-xl font-semibold text-secondary-foreground mb-2">
              Rejoindre la newsletter
            </h3>
            <p className="text-secondary-foreground/80 text-sm mb-4">
              Votre adresse email sera uniquement utilisée pour vous envoyer nos newsletters.
            </p>
            {nlStatus === "success" ? (
              <div className="flex items-center justify-center gap-2 text-white bg-white/20 rounded-full px-6 py-3 max-w-md mx-auto">
                <Check className="h-5 w-5" />
                <span className="font-medium">Merci pour votre inscription !</span>
              </div>
            ) : (
              <form
                onSubmit={handleNewsletterSubmit}
                className="flex gap-2 max-w-md mx-auto"
                suppressHydrationWarning
              >
                <Input
                  type="email"
                  placeholder="Renseignez votre email"
                  className="bg-white"
                  value={nlEmail}
                  onChange={(e) => { setNlEmail(e.target.value); setNlStatus("idle"); }}
                  required
                  suppressHydrationWarning
                />
                <Button type="submit" variant="secondary" disabled={nlStatus === "loading"} className="cursor-pointer">
                  {nlStatus === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : "S'inscrire"}
                </Button>
              </form>
            )}
            {nlStatus === "error" && (
              <p className="text-red-200 text-sm mt-2">{nlError}</p>
            )}
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="bg-primary">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {/* Logo & description */}
            <div className="lg:col-span-2">
              <Link href="/">
                <Image
                  src="/logo_black.svg"
                  alt="Toolouer"
                  width={140}
                  height={40}
                  className="h-10 w-auto mb-4"
                />
              </Link>
              <p className="text-sm text-primary-foreground/80 max-w-sm">
                Toolouer est une marketplace dédiée à la location et à la vente de matériel de chantier.
                Notre mission est de simplifier l&apos;accès à un large choix de machines et d&apos;équipements
                de qualité pour les particuliers et les professionnels tout au long de leurs projets.
              </p>
              {/* Social */}
              <div className="flex gap-4 mt-6">
                <p className="text-sm font-medium text-primary-foreground">Rejoignez nos réseaux sociaux</p>
              </div>
              <div className="flex gap-2 mt-2">
                {socialLinks.map((social) => (
                  <Link
                    key={social.name}
                    href={social.href}
                    className="p-2 rounded-full bg-primary-foreground/10 hover:bg-primary-foreground/20 transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <social.icon className="h-5 w-5 text-primary-foreground" />
                  </Link>
                ))}
              </div>
            </div>

            {/* Links */}
            {Object.entries(footerLinks).map(([key, section]) => (
              <div key={key}>
                <h4 className="font-semibold mb-4 text-primary-foreground">{section.title}</h4>
                <ul className="space-y-2">
                  {section.links.map((link) => (
                    <li key={link.name}>
                      {link.cookie ? (
                        <button
                          onClick={() => {
                            localStorage.removeItem("cookie-consent");
                            window.location.reload();
                          }}
                          className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors cursor-pointer"
                        >
                          {link.name}
                        </button>
                      ) : link.external ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                        >
                          {link.name}
                        </a>
                      ) : (
                        <Link
                          href={link.href}
                          className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors"
                        >
                          {link.name}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Copyright */}
      <Separator />
      <div className="container mx-auto px-4 py-4">
        <p className="text-sm text-muted-foreground text-center">
          © {new Date().getFullYear()} Toolouer. Tous droits réservés.
        </p>
      </div>
    </footer>
  );
}
